import React, { FunctionComponent, useEffect, useState } from "react";
import { EventConstants } from "../../EventConstants";
import { getProperty } from "../../functions/get-property";
import { TYPES } from "../../ioc/types";
import { ApiAction, ApiResponse } from "../../models/http";
import { AjaxService, UrlService } from "../../services";
import { Guid } from "../../util/Guid";
import { useInject } from "../hooks/useInject";

/**
 * Text input search options interface. Configured the searchable text input control.
 */
export interface TextInputSearchOptions {
	input: HTMLInputElement;
	selected: { value?: any };
}

// get data by search text function
const getData = (urlService: UrlService, ajaxService: AjaxService, action: ApiAction, search: string) => {

	action.data = { search };

	const url = urlService.apiAction(action);

	return ajaxService.request<ApiResponse<Array<any>>>({
		url
	});
}

const containerCss = "text-search-results";
const containerVisibleCss = `${containerCss} show`;
const resultCss = "text-search-result";
const focusedResultCss = "focused";

// Text input search state interface, defines the state for the TextInputSearch component.
interface TextInputSearchState {
	searchResults: Array<any>;
	showResults: boolean;
	resultsGuid: string;
	selectedText: string;
}

/**
 * Text input search component, provides search functionality for a text input.
 * @param props Configuration properties.
 * @see TextInputSearchOptions
 */
export const TextInputSearch: FunctionComponent<TextInputSearchOptions> = (props: TextInputSearchOptions) => {

	// create initial state
	const [state, setState] = useState({
		searchResults: [] as Array<any>,
		showResults: false,
		resultsGuid: Guid.newGuid(),
		selectedText: ""
	} as TextInputSearchState);

	// add effects that are exectuted after rendering
	useEffect(() => {

		// function handling a click when results are displayed,
		// if not a search result then hide the results
		const handleBodyClick = (event: MouseEvent) => {

			const target = event.target as HTMLElement;

			if (state.showResults === true && !(target.classList.contains("text-search-result") || target.classList.contains("text-search-results"))) {

				hideResults();
			}
		};

		// add event listener
		if (state.showResults) {

			document.addEventListener(EventConstants.click, handleBodyClick);
		}

		// remove the event listener on tear down
		return () => {
			document.removeEventListener(EventConstants.click, handleBodyClick);
		};
	});

	// get dependencies
	const urlService = useInject<UrlService>(TYPES.UrlService);
	const ajaxService = useInject<AjaxService>(TYPES.AjaxService);

	const input = props.input;
	const textFieldName = input.getAttribute("data-text-field") ?? "Name";
	const valueFieldName = input.getAttribute("data-value-field") ?? "Id";
	const controller = input.getAttribute("data-controller") ?? "";
	const action = input.getAttribute("data-action") ?? "";

	const apiAction = {
		controller,
		action
	};

	if (!input.getAttribute("autocomplete"))
		input.setAttribute("autocomplete", "off");

	let resultElements;

	// if there are results then create a results div element
	if (state.searchResults?.length) {

		resultElements = state.searchResults.map((result) => {
			const value = getProperty(result, valueFieldName);
			return <div className={resultCss} key={value} data-value={value}>{getProperty(result, textFieldName)}</div>
		});
	}
	else {

		resultElements = new Array<JSX.Element>();
	}

	const containerClass = state.showResults ? containerVisibleCss : containerCss;

	const refreshState = () => {
		setState({ ...state });
	}

	// hide results handler for when escape or tab is pressed or a click is made somewhere else on the page
	const hideResults = () => {

		if (state.selectedText && input.value && input.value !== state.selectedText) {
			input.value = state.selectedText;
		}
		else if (!input.value) {
			selectItem("");
		}
		state.showResults = false;
		refreshState();
	}

	// function to handle item selection
	const selectItem = (value: string) => {

		const dataItem = state.searchResults.find(result => getProperty(result, valueFieldName) === value);
		const text = dataItem ? getProperty(dataItem, textFieldName) : "" ?? "";

		props.selected.value = dataItem;
		input.value = text;

		state.selectedText = text;
		state.showResults = false;
		refreshState();

		input.dispatchEvent(new Event("resultselected"));
	}

	// input focus handler, ensures selectedText is set
	input.onfocus = () => {

		if (input.value && state.selectedText !== input.value) {
			state.selectedText = input.value;
			refreshState();
		}
	};

	// on keyboard input handler, performs search as user types functionality to search and display results
	input.oninput = () => {

		const value = input.value;

		if (!value?.trim()) {
			state.showResults = false;
			refreshState();
			return;
		}

		const responsePromise = getData(urlService, ajaxService, apiAction, value);

		responsePromise.then(response => {

			if (response?.isValid && response.result.length > 0) {

				state.searchResults = response.result;
				state.showResults = true;
				refreshState();
			}
			else {

				state.showResults = false;
				refreshState();
			}
		});
	};

	// add keydown event to catch arrow, enter, tab and escape events
	input.onkeydown = event => {

		switch (event.key) {

			// navigates the search results
			case "ArrowDown":
			case "ArrowUp":
				{

					const resultsElement = document.getElementById(state.resultsGuid);
					const resultElements = resultsElement?.querySelectorAll(`.${resultCss}`);

					if (resultsElement && resultElements?.length) {

						const hovered = resultsElement.querySelector(`.${resultCss}.${focusedResultCss}`);

						if (!hovered) {

							const newIndex = event.key === "ArrowDown" ? 0 : resultElements.length - 1;
							resultElements.item(newIndex).classList.add(focusedResultCss);
						}
						else {

							hovered.classList.remove(focusedResultCss);

							let index = state.searchResults.findIndex(result => getProperty(result, valueFieldName) === hovered?.getAttribute("data-value"));
							let newIndex = event.key === "ArrowDown" ? index + 1 : index - 1;

							if (newIndex >= 0 && newIndex < resultElements.length) {

								resultElements.item(newIndex).classList.add(focusedResultCss);
							}
							else {

								newIndex = event.key === "ArrowDown" ? 0 : resultElements.length - 1;
								resultElements.item(newIndex).classList.add(focusedResultCss);
							}
						}
					}

					event.preventDefault();
					break;
				}

			// selects the hovered/navigated result
			case "Enter":
				{
					const resultsElement = document.getElementById(state.resultsGuid);
					const resultElements = resultsElement?.querySelectorAll(`.${resultCss}`);

					if (resultsElement && resultElements?.length) {

						const value = resultsElement.querySelector(`.${resultCss}.${focusedResultCss}`)?.getAttribute("data-value");
						if (value)
							selectItem(value);
					}

					event.preventDefault();
					break;
				}

			// hides the results
			case "Tab":
			case "Escape":
				hideResults();
		}
	};

	// result clicked handler
	const resultClicked = (event: React.MouseEvent) => {

		const element = event.target as HTMLElement;
		const value = element.getAttribute("data-value");
		if (value)
			selectItem(value);
	};

	// output div containing result elements
	return (
		<div id={state.resultsGuid} className={containerClass} onClick={resultClicked}>
			{resultElements}
		</div>
    );
}